import { useCallback, useEffect, useRef, type FocusEvent } from "react";
import { useController, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, CircularProgress, FormControl, FormLabel } from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { FormInput } from "@/components/ui/forms/formInputComponent/formInput.component";
import { LOGIN_FIELD_LABEL_CLASSNAME, LOGIN_TEXT_FIELD_CLASSNAME } from "../loginForm.constants";
import {
	criarSchemaLoginUnificado,
	VALORES_PADRAO_LOGIN_UNIFICADO,
	type FormularioLoginUnificado,
} from "./loginUnificado.schema";
import { formatarEsperaLogin } from "./utils/loginUnificado.utils";
import { ehIdentificadorCpf, obterModoLoginUnificado } from "./utils/usuarioLogin.utils";
import { maskCPF } from "@/utils/maskUtils";
import useFluxoLoginUnificado from "./hooks/useFluxoLoginUnificado";
import useTurnstileLogin from "./hooks/useTurnstileLogin";
import IdentificacaoGrupoLogin from "./ui/IdentificacaoGrupoLogin";
import VerificacaoSegurancaLogin from "./ui/VerificacaoSegurancaLogin";
import DialogoOtpLoginUnificado from "./ui/dialogs/DialogoOtpLoginUnificado";
import DialogoOtpSpaceUnificado from "./ui/dialogs/DialogoOtpSpaceUnificado";
import CampoSenhaLoginUnificado from "./ui/CampoSenhaLoginUnificado";
import type { FormularioEntrarJwtProps } from "../FormularioEntrarJwt";
import clsx from "clsx";

// ------------------------------- COMPONENT -------------------------------
export default function FormularioEntrarUnificadoJwt({ densidade = "normal" }: Readonly<FormularioEntrarJwtProps>) {
	// -------------------------------- STATES --------------------------------
	const entradaAutomaticaRef = useRef<string | null>(null);
	const usernameRef = useRef<HTMLInputElement>(null);
	const passwordRef = useRef<HTMLInputElement>(null);
	const fluxo = useFluxoLoginUnificado();
	const { abrirSpace } = fluxo;
	const verificacao = useTurnstileLogin();

	// --------------------------------- FORM ---------------------------------
	const { control, formState, handleSubmit, setValue, resetField, clearErrors } = useForm<FormularioLoginUnificado>({
		mode: "onChange",
		defaultValues: VALORES_PADRAO_LOGIN_UNIFICADO,
		resolver: zodResolver(criarSchemaLoginUnificado(fluxo.grupo.origem)),
	});
	const { field: codigo } = useController({ control, name: "codigo" });
	const username = useWatch({ control, name: "username" });
	const modo = obterModoLoginUnificado(username);
	const tipoIdentificacao = fluxo.grupo.identificacao?.tipo ?? "codigo";
	const valorIdentificacao = fluxo.grupo.identificacao?.valor ?? codigo.value;
	const grupoValido = tipoIdentificacao === "slug" || /^\d{4}$/.test(valorIdentificacao);
	const chaveEntrada = modo === "otp-space" && grupoValido ? `${tipoIdentificacao}:${valorIdentificacao}` : null;
	const ocupado = fluxo.enviando || fluxo.desafio !== null || fluxo.entradaSpace !== null;
	const espera = fluxo.bloqueio.segundosRestantes;

	// -------------------------------- EFFECTS -------------------------------
	useEffect(() => {
		if (modo === "otp-space") resetField("password");
	}, [modo, resetField]);

	useEffect(() => {
		if (chaveEntrada === null) {
			entradaAutomaticaRef.current = null;
			return;
		}
		if (ocupado || espera > 0 || entradaAutomaticaRef.current === chaveEntrada) return;
		entradaAutomaticaRef.current = chaveEntrada;
		abrirSpace({ tipo: tipoIdentificacao, valor: valorIdentificacao });
	}, [chaveEntrada, ocupado, espera, tipoIdentificacao, valorIdentificacao, abrirSpace]);

	// ----------------------------- DERIVED STATE ----------------------------
	const compacto = densidade === "compacta";
	const erro =
		espera > 0
			? `Muitas tentativas sem sucesso. Aguarde ${espera} segundos para tentar de novo.`
			: fluxo.mensagemErro;

	const desabilitado = ocupado || espera > 0 || !formState.isValid || (modo === "senha" && !verificacao.token);

	let textoBotao = modo === "otp-space" ? "Reabrir OTP" : "Entrar no sistema";
	if (fluxo.enviando) textoBotao = "Entrando…";
	else if (espera > 0) textoBotao = `Aguarde ${formatarEsperaLogin(espera)}`;

	// -------------------------------- HANDLERS ------------------------------
	// O PIN usa este callback em um effect; identidade estável evita roubar o foco em outros renders.
	const focarUsuario = useCallback(() => usernameRef.current?.focus(), []);

	function formatarUsuarioAoSair(evento: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void {
		const usuario = evento.target.value.trim();
		if (!ehIdentificadorCpf(usuario)) return;
		const formatado = maskCPF(usuario);
		if (formatado !== usuario) setValue("username", formatado, { shouldValidate: true, shouldDirty: true });
	}

	async function submeter(dados: FormularioLoginUnificado): Promise<void> {
		if (ocupado || fluxo.bloqueio.estaBloqueado()) return;
		if (modo === "otp-space") {
			fluxo.abrirSpace({ tipo: tipoIdentificacao, valor: valorIdentificacao });
			return;
		}
		const token = verificacao.consumirToken();
		if (!token) return;
		try {
			const resultado = await fluxo.entrar(dados, token);
			if (resultado === null) passwordRef.current?.focus();
		} finally {
			resetField("password");
			verificacao.renovar();
		}
	}

	function trocarGrupo(): void {
		if (ocupado) return;
		fluxo.trocarGrupo();
		setValue("codigo", "", { shouldValidate: false });
		resetField("password");
		clearErrors();
	}

	// -------------------------------- RETURN --------------------------------
	return (
		<>
			<form
				name="loginForm"
				noValidate
				onSubmit={handleSubmit(submeter)}
				className={clsx("flex w-full flex-col", compacto ? "gap-2" : "gap-1")}
				aria-busy={ocupado}
			>
				<div className="mb-3">
					<IdentificacaoGrupoLogin
						densidade={densidade}
						grupo={fluxo.grupo}
						codigo={codigo.value}
						carregando={ocupado}
						aoAlterar={codigo.onChange}
						aoCompletar={focarUsuario}
						aoTrocar={trocarGrupo}
					/>
				</div>

				<FormControl fullWidth>
					<FormLabel htmlFor="username" className={LOGIN_FIELD_LABEL_CLASSNAME}>
						Usuário
					</FormLabel>
					<FormInput<FormularioLoginUnificado>
						control={control}
						name="username"
						id="username"
						placeholder="Digite usuário, ID ou CPF"
						autoComplete="username"
						className={LOGIN_TEXT_FIELD_CLASSNAME}
						inputRef={usernameRef}
						onBlur={formatarUsuarioAoSair}
						autoFocus={fluxo.grupo.origem !== "codigo"}
						disabled={ocupado}
						upperCaseValue
						highlightActiveSelection={false}
						size="small"
						reserveHelperText={!compacto}
						leftIcon={
							<FuseSvgIcon size={16} className="text-[#99aabb]">
								lucide:circle-user-round
							</FuseSvgIcon>
						}
					/>
				</FormControl>
				{modo === "senha" ? (
					<CampoSenhaLoginUnificado
						control={control}
						inputRef={passwordRef}
						carregando={ocupado}
						densidade={densidade}
					/>
				) : null}

				{modo === "senha" ? (
					<VerificacaoSegurancaLogin
						className="mt-1"
						verificacao={verificacao}
						carregando={ocupado}
						action="login"
					/>
				) : null}

				{erro ? <Alert severity="error">{erro}</Alert> : null}

				<button
					type="submit"
					disabled={desabilitado}
					className={clsx(
						"flex w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] border-0 bg-[linear-gradient(110deg,#e6215a,#504a99)] text-[14px] font-bold tracking-[0.08em] text-white uppercase shadow-[0_8px_24px_rgba(230,33,90,.25)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-55 disabled:shadow-none",
						compacto ? "mt-2 h-11" : "mt-6 h-13.5",
					)}
				>
					{fluxo.enviando ? <CircularProgress size={18} color="inherit" /> : null}
					{textoBotao}
				</button>
			</form>

			{fluxo.desafio ? (
				<DialogoOtpLoginUnificado
					key={fluxo.desafio.valor}
					desafio={fluxo.desafio}
					bloqueio={fluxo.bloqueio}
					aoFechar={fluxo.cancelarDesafio}
					aoExpirar={fluxo.expirarDesafio}
				/>
			) : null}
			{fluxo.entradaSpace ? (
				<DialogoOtpSpaceUnificado
					identificacao={fluxo.entradaSpace}
					idGrupoConsulta={fluxo.grupo.idConsultaSpace}
					bloqueio={fluxo.bloqueio}
					aoFechar={fluxo.cancelarDesafio}
					aoExpirar={fluxo.expirarDesafio}
				/>
			) : null}
		</>
	);
}
